import React, { useEffect, useMemo, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { MapPin, ArrowLeft, MessageCircle, Filter, LayoutGrid, List, Search } from 'lucide-react';
import { Slider } from '../components/ui/slider';
import { Checkbox } from '../components/ui/checkbox';
import { destinations, categories } from '../mock';
import { useActivities, useDestinations } from '../hooks/use-activities';
import { useCurrency } from '../context/CurrencyContext';
import ActivityCard from '../components/ActivityCard';
import ActivityListCard from '../components/ActivityListCard';
import Pagination from '../components/Pagination';
import { buildWhatsAppUrl, buildGeneralWhatsAppMessage, hasWhatsAppNumber } from '../lib/whatsapp';
import { useT, useLanguage } from '../i18n/LanguageContext';
import { useLocalizedDestinations } from '../i18n/contentLocale';
import Seo from '../components/Seo';
import { canonicalForLang, buildLangAlternates } from '../lib/seo';

const PAGE_SIZES = [9, 18, 36, 50];

// Category labels share the same taxonomy/translations as /activities.
const CATEGORY_LABEL_KEY = {
  nauticas: 'bestOf.filterWater',
  buceo: 'bestOf.filterDiving',
  aventura: 'bestOf.filterLand',
  aire: 'bestOf.filterAir',
  naturaleza: 'bestOf.filterNature',
  gastronomia: 'bestOf.filterFood',
};

export default function Destination() {
  const { slug } = useParams();
  const t = useT();
  const { lang, currentLanguage } = useLanguage();
  const { currency, format: formatPrice } = useCurrency();
  const localizedDestinations = useLocalizedDestinations(destinations);
  const island = localizedDestinations.find((d) => d.id === slug);

  const [q, setQ] = useState('');
  const [qDebounced, setQDebounced] = useState('');
  const [selectedCategories, setSelectedCategories] = useState([]);
  const [price, setPrice] = useState([0, 1200]);
  const [appliedPrice, setAppliedPrice] = useState([0, 1200]);
  const [sort, setSort] = useState('featured');
  const [view, setView] = useState('list');
  const [showFilters, setShowFilters] = useState(false);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(9);

  // Reset local filters whenever the island itself changes (navigating between
  // destination pages shouldn't carry over the previous island's filters).
  useEffect(() => {
    setQ(''); setQDebounced(''); setSelectedCategories([]);
    setPrice([0, 1200]); setAppliedPrice([0, 1200]); setSort('featured'); setPage(1);
  }, [slug]);

  useEffect(() => {
    const id = setTimeout(() => setQDebounced(q.trim()), 350);
    return () => clearTimeout(id);
  }, [q]);

  const query = useMemo(() => {
    const p = { island: slug, limit: pageSize, skip: (page - 1) * pageSize, sort };
    if (qDebounced) p.q = qDebounced;
    if (selectedCategories.length) p.category = selectedCategories.join(',');
    if (appliedPrice[0] > 0) p.min_price = appliedPrice[0];
    if (appliedPrice[1] < 1200) p.max_price = appliedPrice[1];
    return p;
  }, [slug, pageSize, page, sort, qDebounced, selectedCategories, appliedPrice]);

  const { data, isLoading, isFetching } = useActivities(query);
  const items = data?.items || [];
  const total = data?.total ?? 0;
  const pageCount = Math.max(1, Math.ceil(total / pageSize));

  // Unfiltered count for the whole island, used in the hero stat so it stays
  // stable (doesn't shrink) while the visitor plays with filters below.
  const { data: islandTotals, isLoading: islandTotalsLoading } = useActivities({ island: slug, limit: 1 });
  const islandTotal = islandTotals?.total ?? 0;

  // Indexability uses a different count than the hero stat: a noindexed
  // activity still shows up for visitors (counted in islandTotal above) but
  // shouldn't keep the destination page itself indexable.
  const { data: destinationsList, isLoading: destinationsListLoading } = useDestinations();
  const indexableTotal = destinationsList?.find((d) => d.slug === slug)?.indexable_activity_count;
  const isNoindexable = !destinationsListLoading && indexableTotal === 0;

  useEffect(() => {
    setPage(1);
  }, [qDebounced, selectedCategories, appliedPrice, sort, pageSize]);

  const changePage = (p) => { setPage(p); window.scrollTo({ top: 0, behavior: 'smooth' }); };
  const toggleCategory = (v) =>
    setSelectedCategories((arr) => (arr.includes(v) ? arr.filter((x) => x !== v) : [...arr, v]));

  if (!island) {
    return (
      <div className="pt-40 pb-20 text-center">
        <p className="text-gray-600">{t('places.islandNotFound')}</p>
        <Link to="/destinations" className="text-[#1fa5a3] underline">{t('destinations.viewAll')}</Link>
      </div>
    );
  }

  const whatsappUrl = buildWhatsAppUrl(buildGeneralWhatsAppMessage(t));
  const hasActiveFilters = Boolean(qDebounced) || selectedCategories.length > 0;

  return (
    <div className="pt-20 bg-white min-h-screen">
      {/* Destinations with no real listed activities yet are thin content —
          keep them crawlable (so links still pass through) but out of the
          index until they have something real to show. */}
      {isNoindexable ? (
        <Seo
          title={`${island.name} | OWA Wild Adventure`}
          description={island.description || t('places.comingSoon')}
          canonical={canonicalForLang(`/destinations/${slug}`, lang)}
          alternates={buildLangAlternates(`/destinations/${slug}`)}
          ogLocale={currentLanguage.locale.replace('-', '_')}
          robots="noindex,follow"
        />
      ) : null}
      {/* Island hero */}
      <div className="relative h-[46vh] min-h-[320px] w-full flex items-end overflow-hidden">
        <img src={island.image} alt={island.name} className="absolute inset-0 w-full h-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-t from-[#071a2b]/90 via-[#071a2b]/30 to-transparent" />
        <div className="relative max-w-7xl mx-auto w-full px-5 md:px-8 pb-10 text-white">
          <Link to="/destinations" className="inline-flex items-center gap-1.5 text-sm text-white/80 hover:text-white mb-3">
            <ArrowLeft className="w-4 h-4" /> {t('places.backDest')}
          </Link>
          <div className="flex items-center gap-1.5 text-xs uppercase tracking-widest text-white/80 mb-1">
            <MapPin className="w-3.5 h-3.5 text-[#c8a25a]" /> {t('places.canaries')}
          </div>
          <h1 className="text-4xl md:text-6xl font-bold">{island.name}</h1>
          {island.description ? <p className="mt-3 max-w-2xl text-white/85">{island.description}</p> : null}
          {!islandTotalsLoading ? (
            <div className="mt-5">
              {islandTotal > 0 ? (
                <p>
                  <span className="text-3xl md:text-4xl font-bold text-[#c8a25a]">{islandTotal}</span>{' '}
                  <span className="text-white/85">{t('places.activitiesAvailable')}</span>
                </p>
              ) : (
                <span className="inline-flex items-center rounded-full border border-white/30 bg-white/10 px-4 py-1.5 text-sm font-semibold uppercase tracking-wide text-white/90">
                  {t('places.comingSoon')}
                </span>
              )}
            </div>
          ) : null}
        </div>
      </div>

      {/* Mobile filters toggle */}
      <div className="lg:hidden max-w-7xl mx-auto px-5 md:px-8 pt-8">
        <button
          type="button"
          onClick={() => setShowFilters((v) => !v)}
          aria-expanded={showFilters}
          className="inline-flex items-center gap-2 rounded-full border border-gray-200 bg-white px-5 py-2.5 text-sm font-semibold text-[#14213d] hover:border-[#1fa5a3] hover:text-[#1fa5a3]"
        >
          <Filter className="w-4 h-4" /> {showFilters ? t('activitiesPage.hide') : t('activitiesPage.filters')}
        </button>
      </div>

      <div className="max-w-7xl mx-auto px-5 md:px-8 py-6 lg:py-12 grid grid-cols-1 lg:grid-cols-[280px_1fr] gap-10">
        {/* Filters */}
        <aside className={`space-y-8 bg-[#f7f9fb] p-6 rounded-2xl h-fit lg:sticky lg:top-24 lg:max-h-[calc(100vh-7rem)] lg:overflow-y-auto lg:pr-4 [scrollbar-width:thin] ${showFilters ? 'block' : 'hidden'} lg:block`}>
          <div>
            <div className="flex items-center gap-2 font-semibold text-[#14213d] mb-4">
              <Filter className="w-4 h-4" /> {t('activitiesPage.filters')}
            </div>
            <div className="relative">
              <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
              <input
                value={q}
                onChange={(e) => setQ(e.target.value)}
                placeholder={t('activitiesPage.searchPlaceholder')}
                className="w-full pl-9 pr-3 py-2.5 rounded-lg bg-white border border-gray-200 text-sm focus:outline-none focus:border-[#1fa5a3]"
              />
            </div>
          </div>

          <div>
            <h4 className="font-semibold text-[#14213d] mb-3 text-sm uppercase tracking-wide">{t('activitiesPage.categories')}</h4>
            <div className="space-y-2">
              {categories.map((c) => (
                <label key={c.id} className="flex items-center gap-2 cursor-pointer text-[15px] text-gray-700">
                  <Checkbox
                    checked={selectedCategories.includes(c.id)}
                    onCheckedChange={() => toggleCategory(c.id)}
                  />
                  <span>{CATEGORY_LABEL_KEY[c.id] ? t(CATEGORY_LABEL_KEY[c.id]) : c.name}</span>
                </label>
              ))}
            </div>
          </div>

          <div>
            <h4 className="font-semibold text-[#14213d] mb-4 text-sm uppercase tracking-wide">{t('activitiesPage.price')} ({currency})</h4>
            <Slider min={0} max={1200} step={10} value={price} onValueChange={setPrice} onValueCommit={setAppliedPrice} />
            <div className="mt-3 flex justify-between text-sm text-gray-600">
              <span>{formatPrice(price[0])}</span>
              <span>{formatPrice(price[1])}</span>
            </div>
          </div>
        </aside>

        {/* Results */}
        <div>
          <div className="flex items-end justify-between mb-6 gap-4 flex-wrap">
            <div>
              <h2 className="text-2xl md:text-3xl font-bold text-[#14213d]">
                {t('places.experiencesIn')} {island.name}
              </h2>
              <p className="mt-1 text-gray-600"><span className="font-semibold text-[#14213d]">{total}</span> {t('activitiesPage.resultsFound')}</p>
            </div>
            <Link to={`/activities?destination=${island.id}`} className="text-[#1fa5a3] font-semibold hover:text-[#c8a25a] whitespace-nowrap">
              {t('places.viewCatalog')} →
            </Link>
          </div>

          <div className="flex flex-col sm:flex-row justify-end items-start sm:items-center mb-6 gap-3">
            <div className="flex flex-wrap items-center gap-x-3 gap-y-2">
              <label className="text-sm text-gray-500">{t('activitiesPage.perPage')}</label>
              <select value={pageSize} onChange={(e) => setPageSize(Number(e.target.value))} className="bg-white border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-[#1fa5a3]">
                {PAGE_SIZES.map((n) => <option key={n} value={n}>{n}</option>)}
              </select>
              <div className="hidden sm:flex items-center rounded-lg border border-gray-200 bg-white p-0.5">
                <button
                  type="button"
                  onClick={() => setView('list')}
                  aria-label={t('activitiesPage.viewList')}
                  aria-pressed={view === 'list'}
                  className={`flex h-8 w-8 items-center justify-center rounded-md transition-colors ${view === 'list' ? 'bg-[#1fa5a3] text-white' : 'text-gray-500 hover:text-[#1fa5a3]'}`}
                >
                  <List className="h-4 w-4" />
                </button>
                <button
                  type="button"
                  onClick={() => setView('grid')}
                  aria-label={t('activitiesPage.viewGrid')}
                  aria-pressed={view === 'grid'}
                  className={`flex h-8 w-8 items-center justify-center rounded-md transition-colors ${view === 'grid' ? 'bg-[#1fa5a3] text-white' : 'text-gray-500 hover:text-[#1fa5a3]'}`}
                >
                  <LayoutGrid className="h-4 w-4" />
                </button>
              </div>
              <label className="text-sm text-gray-500">{t('activitiesPage.sort')}</label>
              <select value={sort} onChange={(e) => setSort(e.target.value)} className="bg-white border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-[#1fa5a3]">
                <option value="featured">{t('activitiesPage.sortFeatured')}</option>
                <option value="price-asc">{t('activitiesPage.sortPriceAsc')}</option>
                <option value="price-desc">{t('activitiesPage.sortPriceDesc')}</option>
                <option value="rating">{t('activitiesPage.sortRating')}</option>
              </select>
            </div>
          </div>

          {isLoading ? (
            <div className="space-y-4">
              {Array.from({ length: 3 }).map((_, i) => <div key={i} className="rounded-2xl border border-gray-100 bg-gray-100 h-40 animate-pulse" />)}
            </div>
          ) : items.length ? (
            <>
              <div className={isFetching ? 'opacity-60 transition-opacity' : 'transition-opacity'}>
                {view === 'grid' ? (
                  <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-6">
                    {items.map((a) => <ActivityCard key={a.id} activity={a} />)}
                  </div>
                ) : (
                  <div className="space-y-4">
                    {items.map((a) => <ActivityListCard key={a.id} activity={a} />)}
                  </div>
                )}
              </div>
              <Pagination page={page} pageCount={pageCount} onPageChange={changePage} pageSize={pageSize} onPageSizeChange={setPageSize} />
            </>
          ) : hasActiveFilters ? (
            <div className="rounded-2xl border border-dashed border-gray-200 bg-[#f7f9fb] px-6 py-14 text-center">
              <h3 className="text-xl font-semibold text-[#14213d]">{t('activitiesPage.empty')}</h3>
            </div>
          ) : (
            <div className="rounded-2xl border border-dashed border-gray-200 bg-[#f7f9fb] px-6 py-14 text-center">
              <h3 className="text-xl font-semibold text-[#14213d]">{t('places.preparingTitle')} {island.name}</h3>
              <p className="mt-2 max-w-xl mx-auto text-gray-500">
                {t('places.preparingText')}
              </p>
              <div className="mt-6 flex flex-wrap items-center justify-center gap-3">
                {hasWhatsAppNumber() ? (
                  <a href={whatsappUrl} target="_blank" rel="noreferrer"
                    className="inline-flex items-center gap-2 rounded-full bg-[#25D366] px-6 py-3 text-sm font-semibold text-white hover:brightness-95">
                    <MessageCircle className="w-4 h-4" /> {t('places.whatsapp')}
                  </a>
                ) : null}
                <Link to="/contact" className="inline-flex items-center gap-2 rounded-full border border-gray-200 px-6 py-3 text-sm font-semibold text-[#14213d] hover:border-[#1fa5a3] hover:text-[#1fa5a3]">
                  {t('places.contactUs')}
                </Link>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
